// A student's HOTS sitting: the thinking questions for one sub-topic, scored and saved.
//
// The engine does the selecting. getHots() fetches the child's mastered skills and attempted
// HOTS ids ITSELF and returns the WHOLE ordered set - all-new or all-review, never a blend. It
// is this screen that hands them out five at a time and asks before going on, which is why no
// serveCount is passed. Nothing here filters or re-sorts; re-sorting would break the ordering
// the engine guarantees across index rebuilds.
//
// SAVING: only a Mastery sitting writes. A Practice sitting is a replay of a set the child has
// already finished - they can answer again, but nothing is stored, because only the first
// attempt at a HOTS is ever scored. The screen skips the call; the server's partial unique
// index is the backstop, not the mechanism.

import { AppContext, AdaptivePracticeContext } from '../../../context/context';
import { getTimeSpent } from '../../../utils/utils';
import { Component } from 'react';
import { ContentFactoryImpl, Content } from '@mathbuddy/adaptive-practice-engine';
import { PlayFrame } from '../frame/PlayFrame';
import { ls } from '../../../localStorage';
import apis from '../../../apis/api';
import { applyTheme, Theme } from '../theme';
import { Sitting } from './sitting';

/**
 * The saved shape of one try.
 *
 * The engine hands back whatever the question TEMPLATE produced - an array for the option
 * questions, a bare value for others - and passes it through PlayFrame untouched. The save
 * endpoint's answersDto declares `answer: string[]` and validates it with @IsArray, so a bare
 * value is rejected with "questions.0.answers.0.answer must be an array" and the child's answer
 * is lost.
 *
 * Normalised here rather than loosened in the DTO: every attempt row should hold the same shape
 * whatever kind of question produced it, and adaptive_questions is read back by the reports.
 */
class HotsForStudents extends Component<any, any> {
  static contextType = AppContext;
  context!: { windowHeight: string; windowWidth: string; messageForParent: any; navigateTo: any };

  constructor(props: any) {
    super(props);
    this.state = {
      // HOTS always renders as blueprint. It is not a preference: the look is part of what
      // marks these questions out from ordinary practice, so it is fixed here rather than
      // taken from the payload. If a HOTS sitting ever needs to vary, this is the one line.
      theme: Theme.Blueprint,
      contentFactoryObj: new ContentFactoryImpl(),
      contentObj: new Content(),
      langObj: { number_lang: 'en', lang: '' },
      serve: [],
      position: 0,
      // The frame renders the popup only while isLoader is false, so these two are mutually
      // exclusive. A Mastery sitting opens on the start popup and loads nothing until it is
      // dismissed; a Practice replay has no popup and starts loading at once.
      isLoader: props.sitting !== 'Mastery',
      response: { component: [] },
      // Placeholder only. The clock that time_spent measures is (re)started in renderCurrent,
      // in the same setState that delivers the question and clears the loader - so the start
      // popup, the two getHots calls and the render itself are all outside the measurement.
      startTime: new Date(),
      isFinished: false,
      answered: 0,
      correct: 0,
      // Correct answers that earn Smart XP. Separate from `correct`, which drives the ending
      // popup and counts a Practice replay too - XP is only for scored, first-attempt work.
      smartXpCorrect: 0,
      // Coins earned in THIS sitting, mirroring the server's rule so the child sees the same
      // number the save banked. A Practice replay earns nothing and is not counted.
      coinsEarned: 0,
      // The questions as saved. The host re-runs its own accounting over these - same shape it
      // gets from adaptive practice - rather than trusting a summary computed here.
      answeredQuestions: [],
      // Tries on the question currently on screen. Cleared when it is done with.
      attempts: [],
      // A Mastery sitting opens by explaining what the questions are worth. A Practice replay
      // does not - nothing is at stake, so the prompt would be misleading.
      isShowStartPopup: props.sitting === 'Mastery',
      // What this sitting is worth. SEEDED from the launch payload - the dashboard card worked
      // it out from its own getHots - and replaced by the engine's answer as soon as the
      // sitting loads. The card's view can be stale: the child may have mastered the last skill,
      // or finished the set, between tapping and playing. The engine is asked at entry and is
      // the source of truth; this only decides the start popup before that answer arrives.
      sitting: props.sitting,
      // Why the sitting is paused on a popup, or null when it is not. 'close' is the
      // confirmation behind the close button; 'break' is the every-fifth-question check-in a
      // Practice replay makes. One flag rather than two, because only one can be up at a time
      // and both use the frame's Stop / Continue Playing popup.
      pausePrompt: null as null | 'close' | 'break',
    };
  }

  componentDidMount = () => {
    // Paint the frame ONCE per screen - 21 custom properties, not a per-question cost.
    applyTheme(this.state.theme);
    // Nothing is fetched or rendered while the start popup is up - otherwise the first question
    // sits behind it, visible around its edges, before the child has agreed to begin. The load
    // is kicked off by setShowStartPopup instead, exactly as adaptive practice does it.
    if (!this.state.isShowStartPopup) this.loadSitting();
  };

  /** Ask the engine what this child gets, then render the first question. */
  loadSitting = async () => {
    try {
      this.setState({ isLoader: true });
      const { currentUser, subTopicSkills, grade, lang } = this.props;
      const countryCode = (
        currentUser.classroom_country_code ||
        currentUser.country_code ||
        'IN'
      ).toLowerCase();
      const langObj = { number_lang: 'en', lang: `${lang}_${countryCode}` };

      const userObj = {
        userId: currentUser.id,
        classroomId: currentUser.classroom_id,
        dbToken: ls.getDBToken(),
        token: ls.getToken(),
        env: process.env.REACT_APP_ENV || '',
      };

      // Already sorted by the engine, and NOT capped: both a new sitting and a review come back
      // as the whole set (serveCount defaults to 0 = no cap). This screen walks it BATCH_SIZE at
      // a time with a break between blocks, and the header counts per block.
      // Nothing to slice here; re-sorting would actively break it, since the engine sorts by id
      // so a child's sequence stays the same across index rebuilds.
      const { serve, mode } = await this.state.contentObj.getHots(
        userObj,
        subTopicSkills,
        grade,
      );

      // The engine's own vocabulary, mapped once here so "new"/"review" never travel further.
      // A disagreement with the launch payload is expected rather than exceptional - it means
      // the child's state moved between the dashboard and here - so it is logged, not treated
      // as an error, and the engine wins. Getting this wrong writes a replay as a scored
      // attempt, or drops a scored one on the floor.
      const engineSitting: Sitting | null =
        mode === 'new' ? 'Mastery' : mode === 'review' ? 'Practice' : null;
      if (engineSitting && engineSitting !== this.state.sitting) {
        console.log(
          `hots: sitting changed since the dashboard - launched as ${this.state.sitting}, serving ${engineSitting}`,
        );
      }
      if (engineSitting) this.setState({ sitting: engineSitting });

      if (!serve || serve.length === 0) {
        this.setState({ langObj, isLoader: false, isFinished: true });
        return;
      }

      this.setState({ langObj, serve, position: 0 }, this.renderCurrent);
    } catch (error) {
      // getHots throws when either of its fetches fails. Never treat that as "nothing
      // attempted" - that would re-serve questions the child has already answered.
      //
      // The child is told nothing. A failed fetch is our problem, not theirs, and an error
      // dialog in the middle of a game reads as something THEY broke. Close straight back to
      // student web, where the card is still sitting there to tap again.
      console.log('hots: could not load the sitting', error);
      this.onClose();
    }
  };

  /** Render whichever question `position` points at. */
  renderCurrent = async () => {
    try {
      this.setState({ isLoader: true });
      const current = this.state.serve[this.state.position];
      // The engine paints its own half of the screen - option plates, hint and explanation art,
      // question colours - from themes/*.json, chosen by setTheme. loadSkillBySkillId takes a
      // theme as its 2nd argument; loadHotsQuestionById takes only an id, so without this the
      // engine keeps whatever its module-level global held - 'space' by default. That is why the
      // frame rendered blueprint while the questions inside it stayed space.
      // Set per question, not once on mount: the global is shared with every other screen.
      this.state.contentObj.setTheme(this.state.theme);
      const loaded = this.state.contentObj.loadHotsQuestionById(current.id);
      if (!loaded) {
        // An id the bundled index does not know - a renamed bank entry, or a package that was
        // not rebuilt. Skip rather than stalling the whole sitting on one bad item.
        console.log('hots: unknown question id', current.id);
        this.next();
        return;
      }

      const response = await this.state.contentFactoryObj.displayQuestion(
        this.state.contentObj,
        this.state.contentObj.createContentTemplateObject(loaded.id, 'REACT_WEB'),
        this.state.langObj,
      );

      this.setState({
        response,
        targetQuestionId: loaded.id,
        isLoader: false,
        startTime: new Date(),
        attempts: [],
      });
    } catch (error) {
      // One question failed to draw. Move to the next rather than ending the sitting - the same
      // thing an unknown question id does above, and the child never learns anything went wrong.
      console.log('hots: could not render question', error);
      this.next();
    }
  };

  /** A replay of a finished set - nothing is scored, and it can run past five questions. */
  get isPractice() {
    return this.state.sitting === 'Practice';
  }

  next = () => {
    const position = this.state.position + 1;
    if (position >= this.state.serve.length) {
      this.setState({ isFinished: true, isLoader: false });
      this.sendSmartXp();
      return;
    }

    // Both modes, and never on the last question. The engine serves the whole set; this walks
    // it a block at a time and asks before going on, rather than committing anyone to however
    // many the sub-topic happens to hold. The next question is NOT rendered yet - position
    // moves, and resuming is what draws it.
    if (position % this.batchSize === 0) {
      this.setState({ position, pausePrompt: 'break', isLoader: false });
      return;
    }

    this.setState({ position }, this.renderCurrent);
  };

  /** Coins by which attempt got it right, mirroring HOTS_COINS_BY_ATTEMPT in play-service.
   *  Shown to the child; the server awards independently from the same answers, so the two agree
   *  without the client being trusted for it. A question offers at most two tries. */
  coinsForAttempt = (attemptsUsed: number) => [5, 3][attemptsUsed - 1] ?? 0;

  /** Two tries per question - the second after a hint - exactly as adaptive practice allows. */
  static MAX_ATTEMPTS = 2;

  /**
   * How long to let the answer feedback land before moving on.
   *
   * PlayFrame starts the coin sound and the answer animation the moment an answer is checked.
   * Advancing straight away tears them down mid-play - the browser reports "The play() request
   * was interrupted by a call to pause()" - and on the LAST question the ending popup lands on
   * top of them, so the child gets no sound and no celebration at the one moment it matters.
   *
   * 1700ms, the same wait adaptive practice uses before it shows its own popup.
   */
  static ADVANCE_DELAY_MS = 1700;

  private advanceTimer?: ReturnType<typeof setTimeout>;

  /** Move on once the feedback has played. Used only for a CORRECT answer - a wrong one leaves
   *  the explanation up and waits for the child, so there is nothing to outrun. */
  advanceAfterFeedback = () => {
    this.advanceTimer = setTimeout(this.next, HotsForStudents.ADVANCE_DELAY_MS);
  };

  componentWillUnmount() {
    // The child can close during the wait; firing next() into an unmounted screen is harmless
    // but noisy.
    if (this.advanceTimer) clearTimeout(this.advanceTimer);
  }

  /** Accuracy at or above which the ending celebrates. Below it the popup still reports the
   *  score, just without the fanfare. */
  static CELEBRATE_FROM = 50;

  /** Questions per block before the sitting stops to ask whether to go on. Children get five;
   *  a teacher reviewing content gets ten, since they are scanning rather than being taught.
   *  How many a sitting SERVES is the engine's call - this is only how they are handed out. */
  static BATCH_SIZE = 5;
  static TEACHER_BATCH_SIZE = 10;

  get batchSize() {
    return this.props.batchSize || HotsForStudents.BATCH_SIZE;
  }

  saveQuestionResponse = async (
    answerObj: { givenAnswer: any; actualAnswer: any; isCorrectAns: Boolean },
    clickCount: number,
  ) => {
    const isCorrect = answerObj.isCorrectAns === true;

    // Attempts on THIS question, accumulated the way adaptive practice does: one row per
    // question whose answers[] holds every try, not one row per submit. The server reads the
    // length of this array to decide the award, and the unique index would reject a second row.
    const attempts = [
      ...this.state.attempts,
      { isCorrect, answer: answerObj.givenAnswer },
    ];

    // Stay on the question after a wrong first try so the child can use the hint and answer
    // again. Once it is right, or both tries are spent, the question is DONE - but nothing
    // advances here. The engine renders its explanation and its Next button, and that button is
    // what moves on (loadContent -> next below). Calling next() here swapped the explanation out
    // for the following question before the child could read it.
    const isLastTry = attempts.length >= HotsForStudents.MAX_ATTEMPTS;
    const isQuestionOver = isCorrect || isLastTry;

    if (!isQuestionOver) {
      this.setState({ attempts });
      return;
    }

    // Who moves to the next question depends on how this one ended, because the renderer only
    // draws its Next button INSIDE the explanation box - and that box renders only for a wrong
    // answer (CheckAnswerAdaptivePractice: `disabled && !isCorrectAns`).
    //
    //   wrong, out of tries -> the explanation is on screen with its Next. Wait for it, or the
    //                          child never gets to read why they were wrong.
    //   correct             -> there is no Next to click. Advance, or they are stranded.
    const advanceNow = isCorrect;

    // A Practice sitting is a replay of a finished set: the child may answer, but only the first
    // attempt at a HOTS is ever scored, so nothing is written.
    if (this.state.sitting !== 'Mastery') {
      this.setState(
        (prev: any) => ({
          answered: prev.answered + 1,
          correct: prev.correct + (isCorrect ? 1 : 0),
          attempts: [],
        }),
        advanceNow ? this.advanceAfterFeedback : undefined,
      );
      return;
    }

    const earned = isCorrect ? this.coinsForAttempt(attempts.length) : 0;

    // Built from the CURRENT question before anything advances - targetQuestionId and response
    // both belong to the question just answered.
    const question = {
      user_id: this.props.currentUser.id,
      classroom_id: this.props.currentUser.classroom_id,
      // grade stands where lo_id would on a skill question: it is what the difficulty row is
      // keyed by, since a HOTS owns no skill.
      grade: this.props.grade,
      question_id: this.state.targetQuestionId,
      review_obj: this.state.response.reviewObj,
      number_lang: this.state.langObj.number_lang,
      lang: this.state.langObj.lang,
      // No gain and no loss: a HOTS neither continues nor breaks the streak. Same field and
      // same meaning Rise assessments use, so every consumer that already honours it - the
      // student-web reducer and the server's calculateStreak - needs no HOTS branch.
      isStreakFreeze: true,
      // Every try, in order. answers[0] is the first attempt.
      answers: attempts,
      // The final verdict for the question, not for one try.
      isCorrect,
      blooms: this.state.response.blooms,
      learningGaps: this.state.response.learningGaps || [],
      time_spent: getTimeSpent(this.state.startTime),
    };

    // Counted BEFORE the save, not after awaiting it. The explanation appears the moment the
    // answer is checked, so the child can press Next while the POST is still in flight - and
    // with the counters behind an await, the ending popup rendered with the last question
    // missing from its totals ("4 questions, 75%" after answering five).
    this.setState(
      (prev: any) => ({
        answered: prev.answered + 1,
        correct: prev.correct + (isCorrect ? 1 : 0),
        smartXpCorrect: prev.smartXpCorrect + (isCorrect ? 1 : 0),
        coinsEarned: prev.coinsEarned + earned,
        answeredQuestions: [...prev.answeredQuestions, question],
        attempts: [],
      }),
      advanceNow ? this.advanceAfterFeedback : undefined,
    );

    try {
      await apis._saveHotsQuestionResponse({ questions: [question] });
    } catch (error) {
      // The answer is lost rather than the sitting: moving on is better for the child than
      // stalling on a failed write they cannot act on.
      console.log('hots: save failed', error);
    }
  };

  /**
   * Show or dismiss the confirmation behind the close button.
   *
   * The frame calls this two ways - `setShowAdaptivePopup(true)` from the close icon, and
   * `onContinue` from the popup's "Continue Playing", which is a click handler and so arrives
   * with the EVENT as its first argument. Both are truthy, so this toggles rather than reading
   * what it was passed; that is what adaptive practice does, for the same reason.
   *
   * It was previously wired straight to onClose, which is why the close icon left the sitting
   * immediately instead of asking.
   */
  setShowAdaptivePopup = () => {
    // Nothing to go back to once the sitting is over - the ending popup's only action closes.
    if (this.state.isFinished) {
      this.onClose();
      return;
    }
    // The renderer's Finish button arrives here, through PlayFrame's onFinish. Every question
    // answered means the sitting is over, so show the ending popup rather than a confirmation
    // about leaving. This is also why the popup no longer appears on its own: the last question
    // never calls next(), so nothing sets isFinished until the child asks for it.
    if (this.state.serve.length > 0 && this.state.answered >= this.state.serve.length) {
      this.setState({ isFinished: true, isLoader: false, pausePrompt: null });
      this.sendSmartXp();
      return;
    }

    if (this.state.pausePrompt) {
      // "Continue Playing". A batch break paused BEFORE the next question was drawn, so
      // resuming has to draw it; a close confirmation left the question on screen untouched.
      const wasBreak = this.state.pausePrompt === 'break';
      this.setState({ pausePrompt: null }, wasBreak ? this.renderCurrent : undefined);
      return;
    }
    this.setState({ pausePrompt: 'close' });
  };

  /**
   * Report the sitting's Smart XP.
   *
   * Sent when the sitting ENDS, not when the screen closes - a child who wanders off on the
   * ending popup, or whose tab is closed for them, has still earned it. onClose calls this too
   * as a fallback for stopping early.
   *
   * Safe to call more than once: the counter is zeroed here, so every later call is a no-op.
   * That is the same guard adaptive practice uses when it resets correct_count after sending.
   *
   * NEITHER id travels: question_id would key the session per question - and a HOTS sitting is
   * ONE session however many blocks it ran - while lo_id would name a skill a HOTS does not
   * have. activity_type says what it was instead.
   *
   * isPractice is always false: only a scored first attempt is counted, so there is no
   * post-mastery replay to discount.
   */
  sendSmartXp = () => {
    if (this.state.smartXpCorrect <= 0) return;
    this.context.messageForParent('addSmartXP', {
      user_id: this.props.currentUser.id,
      classroom_id: this.props.currentUser.classroom_id,
      activity_type: 'HOTS',
      correct_count: this.state.smartXpCorrect,
      isPractice: false,
    });
    this.setState({ smartXpCorrect: 0 });
  };

  onClose = () => {
    // "That's enough" during a batch break. It ends the sitting rather than leaving, so the
    // ending popup still reports what they did - with no Continue, because they chose to stop.
    // The close confirmation's Stop falls through below and genuinely leaves.
    if (this.state.pausePrompt === 'break') {
      this.setState({ pausePrompt: null, isFinished: true, isLoader: false });
      this.sendSmartXp();
      return;
    }

    // The host keeps the child's coin total in redux and shows the celebration. Sent before
    // 'close' so the parent has the numbers before it navigates away. A Practice replay earns
    // nothing, so there is nothing to report.
    if (this.state.sitting === 'Mastery' && this.state.answered > 0) {
      // Same shape adaptive practice posts: the questions themselves, so the host runs its own
      // StoreManager accounting over them. hotsExtraCoins is the surplus the server already
      // banked - it is for display and local totals only, never re-awarded.
      this.context.messageForParent('updateUserReducerForHots', {
        questions: this.state.answeredQuestions,
        hotsExtraCoins: this.state.coinsEarned - this.state.correct,
        coinsEarned: this.state.coinsEarned,
      });

      this.sendSmartXp();
    }
    this.context.messageForParent('close');
  };

  render() {
    const total = this.state.serve.length;
    const done = this.state.position;
    // The label names the question the child is LOOKING AT; the bar reports what they have
    // FINISHED. Same split the daily challenge uses ('Question ' + attempted + 1 over a bar of
    // attempted / total): on the first question that reads "1 of 5" above an empty bar, which is
    // both true at once. Tying them together made the first question "0 of 5", which is no
    // question number at all.
    const attempted = Math.min(this.state.answered, total);
    // How many are left of what the child has actually been offered. The engine hands over the
    // whole set but the screen serves it a block at a time, so counting to the end of the set
    // would name questions they have not been shown - "3 more to go" has to mean three.
    const blockStart = Math.floor(done / this.batchSize) * this.batchSize;
    const blockTotal = Math.min(this.batchSize, total - blockStart);
    const blockLeft = Math.max(blockTotal - (done - blockStart), 0);
    /** Did they reach the end, or stop at a break? Both land on the ending popup. */
    const finishedAll = attempted >= total;
    /** Percentage right across the sitting. 0 when nothing was answered, rather than NaN. */
    const accuracy = this.state.answered
      ? Math.round((this.state.correct * 100) / this.state.answered)
      : 0;
    // The header counts within the block the child is being served, not the whole set: a
    // six-HOTS sub-topic read "2 of 6" while the screen was serving five and then asking. At a
    // break or the end it stays on the question just finished, so the last of a block reads
    // "5 of 5" over a full bar rather than jumping to the next block before it is offered.
    const onQuestion = Math.min(
      this.state.isFinished || this.state.pausePrompt === 'break' ? attempted : attempted + 1,
      total,
    );
    const labelBlockStart = Math.floor(Math.max(onQuestion - 1, 0) / this.batchSize) * this.batchSize;
    const labelBlockTotal = Math.min(this.batchSize, total - labelBlockStart);
    const labelBlockDone = Math.min(Math.max(attempted - labelBlockStart, 0), labelBlockTotal);

    return (
      <>
        <AdaptivePracticeContext.Provider value={{
          notificationMessage: '',
          coins: undefined,
          streaks: undefined,
          // Safe Zone is a skill-journey concept; loadHotsQuestionById always returns false.
          isSafeZone: false,
          // The meter slot shows progress through the sitting rather than mastery, which a HOTS
          // has no notion of. Hidden until the first answer, like the meter itself.
          // The bar and its label must measure the SAME thing. They did not: the label counted
          // the question on screen while the bar counted questions finished, so the last
          // question read "5 of 5" over a bar stuck at 80% - and it stayed there through the
          // ending popup, because finishing sets isFinished without moving position.
          //
          // Both now follow the question the child is on, and reach the end together.
          meter: {
            percentage: labelBlockTotal > 0 ? Math.round((labelBlockDone * 100) / labelBlockTotal) : 0,
            level: labelBlockTotal > 0 ? `${onQuestion - labelBlockStart} of ${labelBlockTotal}` : '',
          },
          isLastQuestion: this.state.position >= this.state.serve.length - 1,
          isLoader: this.state.isLoader,
          isCoinAnimation: false,
          isShowStartPopup: this.state.isShowStartPopup,
          // Both endings are the frame's own popup - the same one that celebrates a mastered
          // skill and warns before a Safe Zone - rather than screens of our own.
          isShowAdaptivePopup:
            this.state.isFinished || this.state.pausePrompt !== null,
          // Both pauses want the frame's two-button popup: Stop for Now and Continue Playing.
          isCloseFromClick: this.state.pausePrompt !== null,
          engineObj: this.state.response,
          options: {
            isToggleFullSceren: true,
            // No reload. Adaptive practice regenerates a question's numbers on reload, so the
            // button gives a child a fresh version of the same item; a HOTS is an authored,
            // static question, so there is nothing to regenerate - it would come back identical.
            isShowReload: false,
            // The last question ends the sitting, so its explanation says Finish rather than
            // Next - and the ending popup waits for that click instead of appearing under it.
            showFinishOnLast: true,
            isMusicAllowed: true,
          },
          // What the engine's Next button calls once the child has read the explanation. It
          // advances within the sitting - NOT loadSitting, which refetches from getHots and
          // would drop them back at the first question.
          loadContent: this.next,
          saveQuestionResponse: this.saveQuestionResponse,
          // No failure state here. A sitting that cannot load closes back to the dashboard
          // instead of showing the child an error - see loadSitting.
          getPopUpInfo: () =>
            this.state.pausePrompt && !this.state.isFinished
              ? {
                  // Two different pauses, one popup. The batch break is an offer, so it does not
                  // use the "are you sure" wording - nobody has done anything wrong by stopping,
                  // and everything already answered was saved as they went.
                  heading:
                    this.state.pausePrompt === 'break'
                      ? `${done} of ${total} done. Keep going?`
                      : `${blockLeft} more to go. Leave now?`,
                  // The break is a question, not a report: no score, no accuracy. Those belong
                  // to the ending, which is the only place a result is shown.
                  hideStats: this.state.pausePrompt === 'break',
                  showAnimation: false,
                  totalAttemptedQuestions: this.state.answered,
                  accuracy: this.state.answered
                    ? Math.round((this.state.correct * 100) / this.state.answered)
                    : 0,
                  completedStatusPercentage: total
                    ? Math.round((done * 100) / total)
                    : 0,
                  isShowStartPopup: false,
                  isCloseFromClick: true,
                  stopBtnText:
                    this.state.pausePrompt === 'break' ? "That's enough" : 'Stop for now',
                  // "Continue Playing" is the default and reads oddly against a question the
                  // child has not been asked yet.
                  continueBtnText: 'Keep going',
                }
              : this.state.isShowStartPopup
              ? {
                  heading: 'HOTS questions',
                  showAnimation: false,
                  isHots: true,
                  isShowStartPopup: true,
                  isCloseFromClick: false,
                }
              : {
                  // Two very different endings. Mastery is the first time through and the
                  // answers counted; Practice is a replay of questions already answered, where
                  // nothing was scored - so it must not read as a fresh result.
                  heading:
                    this.state.sitting === 'Mastery'
                      ? finishedAll
                        ? `All ${total} done. Nice work!`
                        : `${attempted} of ${total} done. Nice work!`
                      : finishedAll
                      ? `Looked through all ${total} again - nothing saved this time.`
                      : `Looked through ${attempted} of ${total} again - nothing saved this time.`,
                  // The celebration is earned, not automatic: half right or better. Playing it
                  // over a 20% score would read as praise for a result the child struggled with.
                  // A Practice replay never celebrates - nothing was scored.
                  showAnimation:
                    this.state.sitting === 'Mastery' && accuracy >= HotsForStudents.CELEBRATE_FROM,
                  totalAttemptedQuestions: this.state.answered,
                  accuracy,
                  // Not always 100: "That's enough" at a break lands on this same popup, and a
                  // full bar over "5 of 8 done" is the bar contradicting the heading.
                  completedStatusPercentage: total
                    ? Math.round((attempted * 100) / total)
                    : 0,
                  isShowStartPopup: false,
                  isCloseFromClick: false,
                  // Nothing follows a sitting, so the continue action closes too.
                  stopBtnText: 'Done',
                },
          setShowStartPopup: (status: boolean) => {
            this.setState({ isShowStartPopup: status });
            // Dismissed via "Start Now" - this is the sitting's actual beginning.
            if (!status) this.loadSitting();
          },
          setShowAdaptivePopup: this.setShowAdaptivePopup,
          onclose: this.onClose,
        } as any}>
          <PlayFrame></PlayFrame>
        </AdaptivePracticeContext.Provider>
      </>
    );
  }
}

export default HotsForStudents;
